import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Edit, Trash2, Clock, User, Calendar, Save, X } from 'lucide-react'
import { toast } from 'sonner'
import {
  useExecution,
  useExecutionSteps,
  useUpdateExecution,
  useDeleteExecution,
} from '@/features/executions/api/useExecutions'
import {
  ExecutionStatus,
  getExecutionStatusLabel,
  getExecutionStatusColor,
} from '@/types/execution.types'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString()
}

const ExecutionDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const executionId = parseInt(id || '0', 10)

  const [isEditing, setIsEditing] = useState(false)
  const [editStatus, setEditStatus] = useState<ExecutionStatus | ''>('')
  const [editNotes, setEditNotes] = useState('')
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  // Data: GET /api/executions/{id}, GET /api/executions/{id}/steps
  const { data: execution, isLoading } = useExecution(executionId)
  const { data: steps } = useExecutionSteps(executionId)
  const updateMutation = useUpdateExecution()
  const deleteMutation = useDeleteExecution()

  const startEditing = () => {
    if (!execution) return
    setEditStatus(execution.status)
    setEditNotes(execution.notes || '')
    setIsEditing(true)
  }

  const cancelEditing = () => {
    setIsEditing(false)
    setEditStatus('')
    setEditNotes('')
  }

  // PUT /api/executions/{id}
  const handleSave = async () => {
    if (!editStatus) return
    try {
      await updateMutation.mutateAsync({
        id: executionId,
        data: {
          status: editStatus as ExecutionStatus,
          notes: editNotes.trim() || undefined,
        },
      })
      toast.success('Execution updated')
      setIsEditing(false)
    } catch (err) {
      toast.error('Failed to update execution')
    }
  }

  // DELETE /api/executions/{id}
  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(executionId)
      toast.success('Execution deleted')
      navigate('/executions/test-executions')
    } catch (err) {
      toast.error('Failed to delete execution')
    }
  }

  if (isLoading) {
    return <div className="p-8 text-center">Loading execution details...</div>
  }

  if (!execution) {
    return (
      <div className="p-8 text-center">
        <p className="text-muted-foreground">Execution not found</p>
        <Button variant="link" onClick={() => navigate('/executions/test-executions')}>
          Back to executions
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/executions/test-executions')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Execution #{execution.id}</h1>
            <p className="text-muted-foreground">
              {execution.testplan_name || `Plan #${execution.testplan_id}`}
              {' — '}
              {execution.build_name || `Build #${execution.build_id}`}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button
                variant="outline"
                onClick={cancelEditing}
              >
                <X className="mr-2 h-4 w-4" />
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={updateMutation.isPending}
              >
                <Save className="mr-2 h-4 w-4" />
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={startEditing}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Button>
              <Button
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Status</div>
            <Badge className={`mt-1 ${getExecutionStatusColor(execution.status)}`}>
              {getExecutionStatusLabel(execution.status)}
            </Badge>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground flex items-center gap-1">
              <User className="h-3 w-3" /> Tester
            </div>
            <p className="mt-1 font-medium">
              {execution.tester_name || execution.tester_id || 'Unassigned'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Executed
            </div>
            <p className="mt-1 font-medium">{formatDate(execution.execution_ts)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" /> Duration
            </div>
            <p className="mt-1 font-medium">
              {execution.execution_duration
                ? `${execution.execution_duration}s`
                : '-'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Execution Details */}
      <Card>
        <CardHeader>
          <CardTitle>Execution Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Test Case
              </label>
              <p className="mt-1">
                {execution.testcase_name || `TC-${execution.tcversion_id}`}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Platform
              </label>
              <p className="mt-1">
                {execution.platform_name || execution.platform_id || '-'}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Executed On
              </label>
              <p className="mt-1">{formatDate(execution.execution_ts)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Execution Type
              </label>
              <p className="mt-1">
                {execution.execution_type === 1 ? 'Manual' : 'Automated'}
              </p>
            </div>
          </div>

          {/* Status — editable */}
          <div>
            <label className="text-sm font-medium text-muted-foreground">
              Status
            </label>
            {isEditing ? (
              <Select
                value={editStatus}
                onValueChange={(v) => setEditStatus(v as ExecutionStatus)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ExecutionStatus.Passed}>Passed</SelectItem>
                  <SelectItem value={ExecutionStatus.Failed}>Failed</SelectItem>
                  <SelectItem value={ExecutionStatus.Blocked}>Blocked</SelectItem>
                  <SelectItem value={ExecutionStatus.NotRun}>Not Run</SelectItem>
                  <SelectItem value={ExecutionStatus.Pending}>Pending</SelectItem>
                  <SelectItem value={ExecutionStatus.Running}>Running</SelectItem>
                  <SelectItem value={ExecutionStatus.Cancelled}>Cancelled</SelectItem>
                  <SelectItem value={ExecutionStatus.Error}>Error</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <div className="mt-1">
                <Badge className={getExecutionStatusColor(execution.status)}>
                  {getExecutionStatusLabel(execution.status)}
                </Badge>
              </div>
            )}
          </div>

          {/* Notes — editable */}
          <div>
            <label className="text-sm font-medium text-muted-foreground">
              Notes
            </label>
            {isEditing ? (
              <Textarea
                className="mt-1"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Execution notes..."
                rows={4}
              />
            ) : (
              <p className="mt-1 whitespace-pre-wrap">
                {execution.notes || 'No notes'}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Execution Steps — GET /api/executions/{id}/steps */}
      {steps && steps.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Step Results ({steps.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px]">Step</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Expected Result</TableHead>
                  <TableHead className="w-[120px]">Status</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {steps.map((step) => (
                  <TableRow key={step.id}>
                    <TableCell className="font-mono">
                      {step.step_number || step.tcstep_id}
                    </TableCell>
                    <TableCell className="whitespace-pre-wrap">
                      {step.actions || '-'}
                    </TableCell>
                    <TableCell className="whitespace-pre-wrap">
                      {step.expected_results || '-'}
                    </TableCell>
                    <TableCell>
                      {step.status ? (
                        <Badge className={getExecutionStatusColor(step.status)}>
                          {getExecutionStatusLabel(step.status)}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-pre-wrap">
                      {step.notes || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Related Links */}
      <Card>
        <CardHeader>
          <CardTitle>Related</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" size="sm" asChild>
              <Link to={`/tests/test-plans/${execution.testplan_id}`}>
                View Test Plan
              </Link>
            </Button>
            {execution.build_id && (
              <Button variant="outline" size="sm" asChild>
                <Link to="/tests/builds">View Builds</Link>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Execution</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete execution #{executionId} and all associated
              step results. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default ExecutionDetailPage
